<template>
    <div>
        <!-- Editor -->
        <froala
            v-if="!disabled"
            :value="value"
            :config="editor_config()"
            :onManualControllerReady="initialize"
            @input="$emit('input', $event)"
            class="editor content"
            ref="froala"/>

        <froalaView v-else :value="value" class="editor content"/>

        <!-- Chart Dialog -->
        <base-modal :visible="show_chart_dialog" @close="close_modal" title="Chart Parameters">
            <div class="field is-horizontal">
                <div class="field-body">
                    <base-input
                        v-model="chart_data.start_date"
                        type="date"
                        label="Start date"
                        validate="required"
                        name="start_date"/>

                    <base-input
                        v-model="chart_data.end_date"
                        type="date"
                        label="End date"
                        validate="required"
                        name="end_date"/>
                </div>
            </div>

            <base-input
                v-model="chart_data.name"
                label="Name of series"
                validate="required"
                name="name"/><br/>

            <base-button
                v-if="editing_chart_element"
                @click="update_chart"
                icon="fas fa-check"
                class="is-primary"
                text="Update"/>

            <base-button
                v-else
                @click="insert_chart"
                icon="fas fa-check"
                class="is-primary"
                text="Insert"/>
        </base-modal>
    </div>
</template>

<script>
/* Warning: Imperative code!! (Integrating jQuery plugin with VueJS) */
// TODO: listen undo event to save removed image

import { IMAGES_LIST } from '../../api';
import { auth } from '../../utils/requests';
import { chart_html, load_chart } from '../../utils/charts';

export default {
    name: 'Editor',
    props: ['space', 'note', 'value', 'disabled'],
    data() {
        return {
            chart_data: {},
            show_chart_dialog: false,
            editing_chart_element: null,
            editor: null,
        };
    },
    methods: {
        initialize(initControls) {
            initControls.initialize();
            this.editor = initControls.getEditor();
        },
        editor_config() {
            const url = IMAGES_LIST.replace('{space_id}', this.space.id);

            return {
                // initOnClick: true,
                key: process.env.VUE_APP_FROALA_KEY,
                iconsTemplate: 'font_awesome_5',
                imageUpload: true,
                // imageUploadRemoteUrls: false,
                imageUploadParam: 'file',
                imageDefaultAlign: 'left',
                imageManagerLoadURL: null,
                imageUploadURL: url,
                imageUploadMethod: 'POST',
                imageMaxSize: 5 * 1024 * 1024,
                imageAllowedTypes: ['jpeg', 'jpg', 'png'],
                imageEditButtons: [
                    'imageAlign',
                    'imageDisplay',
                    'imageStyle',
                    'imageCaption',
                    'imageLink',
                    'linkOpen',
                    'linkEdit',
                    'linkRemove',
                ],
                linkInsertButtons: ['linkBack', '|'],
                requestHeaders: auth().headers,
                heightMin: '75vh',
                toolbarButtons: [
                    'paragraphFormat', 'fontFamily', '|',
                    'bold', 'italic', 'underline', 'strikeThrough', '|',
                    'textColor', 'backgroundColor', '|',
                    'outdent', 'indent', 'align', '|',
                    'insertImage', 'insertLink', 'insertTable', 'chart', '|',
                    'formatOL', 'formatUL', '|',
                    'quote', 'insertHR', 'html'
                ],
                toolbarSticky: false,
                events: {
                    initialized: (event, editor) => {
                        this.editor = editor;
                        this.render_all_charts();
                    },
                    blur: () => {
                        this.$emit('blur');
                    },
                    'chart.insert': () => {
                        // On new chart insert
                        this.chart_data = {};
                        this.show_chart_dialog = true;
                    },
                },
            };
        },
        async render_all_charts() {
            this.$refs.froala.$el.querySelectorAll('.fr-element .chart')
                .forEach(this.render_chart);
        },
        async render_chart(element) {
            // eslint-disable-next-line no-param-reassign
            element.innerHTML = 'Loading...';
            await load_chart(element);
            element.querySelector('.edit-btn').addEventListener('click', this.edit_chart);
        },
        async insert_chart() {
            // get html chart with unique id
            const { html, id } = chart_html({ type: 'bar', ...this.chart_data });
            // Insert html to editor
            this.editor.html.insert(html);
            // Get just inserted chart by unique id
            const element = this.$refs.froala.$el.querySelector(`div[data-id^="${id}"]`);
            // load data from server and render chart
            await this.render_chart(element);

            this.force_update();
            this.close_modal();
        },
        async update_chart() {
            const element = this.editing_chart_element;
            element.dataset.params = JSON.stringify({ type: 'bar', ...this.chart_data });
            await this.render_chart(element);
            this.force_update();
            this.close_modal();
        },
        edit_chart(event) {
            const element = event.target.parentElement;
            this.chart_data = JSON.parse(event.target.parentElement.dataset.params || '{}');
            this.editing_chart_element = element;
            this.show_chart_dialog = true;
        },
        close_modal() {
            this.chart_data = {};
            this.show_chart_dialog = false;
            this.editing_chart_element = null;
        },
        force_update() {
            // FIXME: Ugly hack
            // Yes I know it is terrible solution.
            // But at the moment I don't know any other way to trigger change event on chart edit.
            this.editor.events.trigger('blur');
        },
    },
};
</script>

<style scoped>
    .editor >>> .fr-wrapper {
        border: none !important;
        z-index: 0 !important;
    }

    .editor >>> .fr-toolbar {
        border: none !important;
        position: sticky;
        z-index: 1;
    }

    .editor >>> .fr-btn-grp {
        border-right: 1px #f9f6f6 solid !important;
        padding-right: 0.3rem !important;
        margin: 0 5px 0 0;
    }

    .editor >>> .fr-btn {
        margin: 0 !important;
    }

    .editor >>> .second-toolbar {
        display: none !important;
    }

    @media print {
        .editor >>> .fr-toolbar {
            display: none !important;
        }

        .editor {
            font-size: 0.9rem;
            line-height: 1.3rem;
        }
    }

    /* CONTENT */

    .editor >>> table td {
        border-width: 1px;
    }

    .editor >>> table tbody tr:last-child td {
        border-bottom-width: 1px !important;
    }

    /* CHART */
    .editor >>> .chart {
        background: #fff;
    }

    .editor >>> .chart {
        position: relative;
    }

    .editor >>> .chart .edit-btn, .editor >>> .chart .move-btn  {
        position: absolute;
        cursor: pointer;
        top: 1.3rem;
        background: #dbdbdb;
        padding: 0.7rem;
        border-radius: 0.2rem;
        display: none;
    }

    .editor >>> .chart .edit-btn {
        left: 3rem;
    }

    .editor >>> .chart .move-btn  {
        left: 6rem;
    }

    .editor >>> .chart:hover .edit-btn {
        display: block;
    }

    .editor >>> .chart:hover .move-btn {
        display: block;
    }
</style>
